import {
  faBolt,
  faBuilding,
  faSync,
  faUsers,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Badge,
  Button,
  Col,
  Container,
  Input,
  Row,
  Table,
} from "reactstrap";
import { useCompanyCore } from "../../cores/company";
import { CompanyPayload } from "../../cores/company/interfaces/api/get-companies";
import socialApiClient from "../../cores/helpers/services/social-api-client";
import { useMeCore } from "../../cores/me";
import { Company } from "../../cores/me/interfaces/api/get-meCompany";
import { useRouterCore } from "../../cores/router";
import { User } from "../../cores/session/interfaces/models/user";
import { userApiService } from "../../cores/user/services/api";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import { WembiiAdminAiUsageSection } from "../component/admin/WembiiAdminAiUsageSection";
import { WembiiAdminDashboardCharts } from "../component/admin/WembiiAdminDashboardCharts";
import { WembiiAdminDemoRequestsSection } from "../component/admin/WembiiAdminDemoRequestsSection";
import "../styles/WembiiAdminDashboard.scss";

function truncateChartLabel(name: string, max = 28): string {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

function parseNanobananaCredits(payload: unknown): number | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as { credits?: unknown; data?: unknown };
  if (typeof p.credits === "number") return p.credits;
  if (typeof p.data === "number") return p.data;
  return null;
}

type CompanyExtras = Company & { createdAt?: string; created_at?: string };

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfDayMs(ymd: string): number {
  return new Date(`${ymd}T00:00:00`).getTime();
}

function endOfDayMs(ymd: string): number {
  return new Date(`${ymd}T23:59:59.999`).getTime();
}

function getCompanyCreatedMs(c: Company): number | null {
  const ex = c as CompanyExtras;
  const raw = ex.createdAt || ex.created_at;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

function rowMatchesDateRange(
  row: CompanyPayload,
  dateFrom: string,
  dateTo: string
): boolean {
  if (!dateFrom && !dateTo) return true;
  const ms = getCompanyCreatedMs(row.company);
  if (ms === null) return false;
  if (dateFrom && ms < startOfDayMs(dateFrom)) return false;
  if (dateTo && ms > endOfDayMs(dateTo)) return false;
  return true;
}

function getUserCreatedMs(u: User): number | null {
  const raw = (u as User & { createdAt?: string; created_at?: string })
    .createdAt || (u as { created_at?: string }).created_at;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

function userMatchesDateRange(
  u: User,
  dateFrom: string,
  dateTo: string
): boolean {
  if (!dateFrom && !dateTo) return true;
  const ms = getUserCreatedMs(u);
  if (ms === null) return false;
  if (dateFrom && ms < startOfDayMs(dateFrom)) return false;
  if (dateTo && ms > endOfDayMs(dateTo)) return false;
  return true;
}

const CHUNK = 8;

const USER_FETCH_CAP = 500;

function computeTrialDaysLeft(owner: User | undefined): number | null {
  if (!owner?.freeTrial || !owner.freeTrialDate) return null;
  const total = owner.freeTrialDays ?? 0;
  const elapsed = moment().diff(moment(owner.freeTrialDate), "days");
  return total - elapsed;
}

function pickLastLoginRaw(u: User | undefined): string | undefined {
  if (!u) return undefined;
  return u.lastLogin || u.last_login || u.lastAccessAt;
}

function mergeOwnerWithUserMap(
  owner: User | undefined,
  usersById: Map<string, User>
): User | undefined {
  if (!owner) return undefined;
  const fromList = usersById.get(owner.id);
  if (!fromList) return owner;
  return { ...owner, ...fromList };
}

async function fetchCreditsMap(
  companyIds: string[],
  onProgress?: (done: number, total: number) => void
): Promise<Map<string, number>> {
  const map = new Map<string, number>();
  let done = 0;
  for (let i = 0; i < companyIds.length; i += CHUNK) {
    const slice = companyIds.slice(i, i + CHUNK);
    await Promise.all(
      slice.map(async (id) => {
        try {
          const { data } = await socialApiClient().get("nanobanana/credits", {
            params: { companyId: id },
          });
          const n = parseNanobananaCredits(data?.payload);
          if (n !== null) map.set(id, n);
        } catch {
          /* ignore per company */
        } finally {
          done += 1;
          onProgress?.(done, companyIds.length);
        }
      })
    );
  }
  return map;
}

export default function WembiiAdminDashboard() {
  const intl = useIntl();
  const { user, isAdmin } = useMeCore();
  const { goToSocial } = useRouterCore();
  const { companies, getCompanies } = useCompanyCore();

  const [userTotal, setUserTotal] = useState<number>(0);
  const [userSample, setUserSample] = useState<User[]>([]);
  const [usersById, setUsersById] = useState<Map<string, User>>(
    () => new Map()
  );
  const [creditsByCompany, setCreditsByCompany] = useState<Map<string, number>>(
    () => new Map()
  );
  const [creditsProgress, setCreditsProgress] = useState<string>("");
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("__all__");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<{
    key: "name" | "plan" | "credits";
    dir: "asc" | "desc";
  }>({ key: "name", dir: "asc" });

  useEffect(() => {
    if (!user?.userRole?.admin) goToSocial();
  }, [user, goToSocial]);

  useEffect(() => {
    if (!isAdmin) return;
    getCompanies({});
  }, [getCompanies, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const head = await userApiService.getUsers({ perPage: 1, page: 1 });
        if (cancelled) return;
        const total = head.total;
        setUserTotal(total);
        if (total === 0) {
          setUserSample([]);
          setUsersById(new Map());
          return;
        }
        const per = Math.min(USER_FETCH_CAP, total);
        const page = await userApiService.getUsers({ perPage: per, page: 1 });
        if (cancelled) return;
        setUserSample(page.users.slice(0, 40));
        setUsersById(new Map(page.users.map((u) => [u.id, u])));
      } catch {
        if (!cancelled) {
          setUserTotal(0);
          setUserSample([]);
          setUsersById(new Map());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const dateFilterActive = Boolean(dateFrom || dateTo);

  const companiesByDate = useMemo(
    () => companies.filter((row) => rowMatchesDateRange(row, dateFrom, dateTo)),
    [companies, dateFrom, dateTo]
  );

  const clearDateFilters = useCallback(() => {
    setDateFrom("");
    setDateTo("");
  }, []);

  const setPresetLastDays = useCallback((days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateFrom(toYmd(from));
    setDateTo(toYmd(to));
  }, []);

  const setPresetThisMonth = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateFrom(toYmd(start));
    setDateTo(toYmd(now));
  }, []);

  const userSampleFiltered = useMemo(
    () => userSample.filter((u) => userMatchesDateRange(u, dateFrom, dateTo)),
    [userSample, dateFrom, dateTo]
  );

  const reloadCredits = useCallback(async () => {
    if (!companies.length) return;
    setLoadingCredits(true);
    const ids = companies.map(({ company: c }) => c.id);
    setCreditsProgress(`0/${ids.length}`);
    try {
      const map = await fetchCreditsMap(ids, (done, total) => {
        setCreditsProgress(`${done}/${total}`);
      });
      setCreditsByCompany(map);
    } finally {
      setLoadingCredits(false);
      setCreditsProgress("");
    }
  }, [companies]);

  const companyIdsKey = useMemo(
    () =>
      companies
        .map(({ company: c }) => c.id)
        .sort()
        .join(","),
    [companies]
  );

  useEffect(() => {
    if (!isAdmin || !companyIdsKey) return;
    reloadCredits();
  }, [isAdmin, companyIdsKey, reloadCredits]);

  const planOptions = useMemo(() => {
    const names = new Set<string>();
    companiesByDate.forEach((row) => {
      if (row.plan?.name) names.add(row.plan.name);
    });
    return Array.from(names).sort();
  }, [companiesByDate]);

  const totalAiCredits = useMemo(() => {
    let s = 0;
    companiesByDate.forEach(({ company: c }) => {
      const v = creditsByCompany.get(c.id);
      if (v !== undefined) s += v;
    });
    return s;
  }, [creditsByCompany, companiesByDate]);

  const planCounts = useMemo(() => {
    const m: Record<string, number> = {};
    companiesByDate.forEach((row) => {
      const k = row.plan?.name || "—";
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [companiesByDate]);

  const chartPlans = useMemo(() => {
    const entries = Object.entries(planCounts).sort((a, b) => b[1] - a[1]);
    return {
      labels: entries.map(([name]) =>
        name.startsWith("app.") ? intl.formatMessage({ id: name }) : name
      ),
      values: entries.map(([, n]) => n),
    };
  }, [planCounts, intl]);

  const chartCreditsTop = useMemo(() => {
    const rows = companiesByDate
      .map((r) => ({
        name: r.company.name,
        credits: creditsByCompany.get(r.company.id),
      }))
      .filter(
        (x): x is { name: string; credits: number } =>
          typeof x.credits === "number" && x.credits >= 0
      )
      .sort((a, b) => b.credits - a.credits)
      .slice(0, 10);
    return {
      labels: rows.map((r) => truncateChartLabel(r.name)),
      values: rows.map((r) => r.credits),
    };
  }, [companiesByDate, creditsByCompany]);

  const chartSignupsByMonth = useMemo(() => {
    const m: Record<string, number> = {};
    companiesByDate.forEach(({ company: c }) => {
      const ex = c as CompanyExtras;
      const raw = ex.createdAt || (c as { created_at?: string }).created_at;
      if (!raw) return;
      const key = moment(raw).format("YYYY-MM");
      m[key] = (m[key] || 0) + 1;
    });
    const keys = Object.keys(m).sort();
    return {
      labels: keys.map((k) =>
        moment(k, "YYYY-MM").locale(intl.locale).format("MMM YYYY")
      ),
      values: keys.map((k) => m[k]),
    };
  }, [companiesByDate, intl.locale]);

  const freeTrialRows = useMemo(() => {
    const rows: {
      row: CompanyPayload;
      daysLeft: number | null;
      ownerResolved: User | undefined;
    }[] = [];
    companies.forEach((row) => {
      const o = row.owner;
      if (!o?.freeTrial) return;
      const merged = mergeOwnerWithUserMap(o, usersById);
      const daysLeft = computeTrialDaysLeft(merged);
      rows.push({ row, daysLeft, ownerResolved: merged });
    });
    rows.sort((a, b) => {
      const da = a.daysLeft ?? -9999;
      const db = b.daysLeft ?? -9999;
      return da - db;
    });
    return rows;
  }, [companies, usersById]);

  const activeFreeTrialCount = useMemo(
    () => freeTrialRows.filter((r) => (r.daysLeft ?? -1) > 0).length,
    [freeTrialRows]
  );

  const filteredRows = useMemo(() => {
    let rows = [...companiesByDate];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(({ company: c }) =>
        c.name.toLowerCase().includes(q)
      );
    }
    if (planFilter !== "__all__") {
      rows = rows.filter((r) => r.plan?.name === planFilter);
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (sort.key === "name") {
        return (
          dir * a.company.name.localeCompare(b.company.name, undefined, {
            sensitivity: "base",
          })
        );
      }
      if (sort.key === "plan") {
        const pa = a.plan?.name || "";
        const pb = b.plan?.name || "";
        return dir * pa.localeCompare(pb);
      }
      const ca = creditsByCompany.get(a.company.id) ?? -1;
      const cb = creditsByCompany.get(b.company.id) ?? -1;
      return dir * (ca - cb);
    });
    return rows;
  }, [companiesByDate, search, planFilter, sort, creditsByCompany]);

  const toggleSort = (key: typeof sort.key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  if (!isAdmin) return null;

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.adminDashboard.title" })}
      />
      <Content>
        <Container fluid className="wembii-admin-dashboard wembii-admin-dashboard--ai py-3">
          <div className="wembii-admin-hero mb-4">
            <div className="wembii-admin-hero__badge">
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              <span>
                {intl.formatMessage({ id: "app.adminDashboard.heroBadge" })}
              </span>
            </div>
            <p className="wembii-admin-hero__subtitle mb-0">
              {intl.formatMessage({ id: "app.adminDashboard.subtitle" })}
            </p>
          </div>

          <Row className="g-3 mb-4">
            <Col xs="12" md="6" xl="3">
              <div className="wembii-admin-kpi wembii-admin-kpi--companies">
                <div className="wembii-admin-kpi__icon">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <div className="wembii-admin-kpi__text">
                  <div className="wembii-admin-kpi__label">
                    {intl.formatMessage({ id: "app.adminDashboard.kpi.companies" })}
                  </div>
                  <div className="wembii-admin-kpi__value">{companiesByDate.length}</div>
                  {dateFilterActive && (
                    <small className="wembii-admin-kpi__hint">
                      {intl.formatMessage({ id: "app.adminDashboard.kpi.dateFilterHint" })}
                    </small>
                  )}
                </div>
              </div>
            </Col>
            <Col xs="12" md="6" xl="3">
              <div className="wembii-admin-kpi wembii-admin-kpi--users">
                <div className="wembii-admin-kpi__icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="wembii-admin-kpi__text">
                  <div className="wembii-admin-kpi__label">
                    {intl.formatMessage({ id: "app.adminDashboard.kpi.users" })}
                  </div>
                  <div className="wembii-admin-kpi__value">{userTotal}</div>
                  {dateFilterActive && (
                    <small className="wembii-admin-kpi__hint">
                      {intl.formatMessage({ id: "app.adminDashboard.kpi.usersTotalHint" })}
                    </small>
                  )}
                </div>
              </div>
            </Col>
            <Col xs="12" md="6" xl="3">
              <div className="wembii-admin-kpi wembii-admin-kpi--credits">
                <div className="wembii-admin-kpi__icon">
                  <FontAwesomeIcon icon={faBolt} />
                </div>
                <div className="wembii-admin-kpi__text">
                  <div className="wembii-admin-kpi__label">
                    {intl.formatMessage({
                      id: dateFilterActive
                        ? "app.adminDashboard.kpi.aiCreditsInRange"
                        : "app.adminDashboard.kpi.aiCreditsTotal",
                    })}
                  </div>
                  <div className="wembii-admin-kpi__value">
                    {loadingCredits ? "…" : totalAiCredits}
                  </div>
                  {loadingCredits && creditsProgress && (
                    <small className="wembii-admin-kpi__hint">
                      {intl.formatMessage({ id: "app.adminDashboard.loadingCredits" })}{" "}
                      {creditsProgress}
                    </small>
                  )}
                  <div className="wembii-admin-kpi__action">
                    <Button
                      color="link"
                      className="p-0 small wembii-admin-refresh"
                      onClick={reloadCredits}
                      disabled={loadingCredits}
                    >
                      <FontAwesomeIcon icon={faSync} className="mr-1" />
                      {intl.formatMessage({ id: "app.adminDashboard.refreshCredits" })}
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs="12" md="6" xl="3">
              <div className="wembii-admin-kpi wembii-admin-kpi--plans">
                <div className="wembii-admin-kpi__text wembii-admin-kpi__text--plans">
                  <div className="wembii-admin-kpi__label wembii-admin-kpi__label--block">
                    {intl.formatMessage({
                      id: dateFilterActive
                        ? "app.adminDashboard.kpi.plansTitleFiltered"
                        : "app.adminDashboard.kpi.plansTitle",
                    })}
                  </div>
                <ul className="list-unstyled mb-0 wembii-admin-kpi__plan-list">
                  {Object.entries(planCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, n]) => (
                      <li key={name} className="d-flex justify-content-between">
                        <span className="text-truncate mr-2">
                          {name.startsWith("app.") ? intl.formatMessage({ id: name }) : name}
                        </span>
                        <strong>{n}</strong>
                      </li>
                    ))}
                </ul>
                </div>
              </div>
            </Col>
          </Row>

          <WembiiAdminDashboardCharts
            planLabels={chartPlans.labels}
            planValues={chartPlans.values}
            creditsLabels={chartCreditsTop.labels}
            creditsValues={chartCreditsTop.values}
            signupLabels={chartSignupsByMonth.labels}
            signupValues={chartSignupsByMonth.values}
          />

          <WembiiAdminAiUsageSection />

          <WembiiAdminDemoRequestsSection />

          <div className="mb-5">
            <h5 className="wembii-admin-section-title mb-2">
              {intl.formatMessage({ id: "app.adminDashboard.section.freeTrial" })}
              {activeFreeTrialCount > 0 ? (
                <Badge color="primary" pill className="ml-2 align-middle">
                  {intl.formatMessage(
                    { id: "app.adminDashboard.freeTrial.activeCount" },
                    { count: activeFreeTrialCount }
                  )}
                </Badge>
              ) : null}
            </h5>
            <p className="small text-muted mb-3">
              {intl.formatMessage({ id: "app.adminDashboard.freeTrial.intro" })}
              {userTotal > USER_FETCH_CAP ? (
                <>
                  {" "}
                  {intl.formatMessage(
                    { id: "app.adminDashboard.freeTrial.hintUserCap" },
                    { cap: USER_FETCH_CAP }
                  )}
                </>
              ) : null}
            </p>
            {freeTrialRows.length === 0 ? (
              <p className="small text-muted mb-0">
                {intl.formatMessage({ id: "app.adminDashboard.freeTrial.empty" })}
              </p>
            ) : (
              <div className="table-responsive wembii-admin-table-wrap">
                <Table size="sm" className="wembii-admin-table">
                  <thead>
                    <tr>
                      <th>
                        {intl.formatMessage({ id: "app.adminDashboard.table.company" })}
                      </th>
                      <th>
                        {intl.formatMessage({ id: "app.adminDashboard.freeTrial.owner" })}
                      </th>
                      <th className="text-right">
                        {intl.formatMessage({ id: "app.adminDashboard.freeTrial.daysLeft" })}
                      </th>
                      <th>
                        {intl.formatMessage({ id: "app.adminDashboard.freeTrial.lastLogin" })}
                      </th>
                      <th>
                        {intl.formatMessage({ id: "app.adminDashboard.table.plan" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {freeTrialRows.map(({ row, daysLeft, ownerResolved }) => {
                      const o = ownerResolved;
                      const lastRaw = pickLastLoginRaw(o);
                      const lastDisplay = lastRaw
                        ? (() => {
                            const d = new Date(lastRaw);
                            return Number.isNaN(d.getTime())
                              ? lastRaw
                              : d.toLocaleString(intl.locale);
                          })()
                        : null;
                      const active = (daysLeft ?? -1) > 0;
                      const badgeColor =
                        (daysLeft ?? 0) > 7
                          ? "success"
                          : (daysLeft ?? 0) > 2
                            ? "warning"
                            : "danger";
                      return (
                        <tr key={row.company.id}>
                          <td className="font-weight-medium">{row.company.name}</td>
                          <td className="small">
                            {o
                              ? `${o.firstName} ${o.lastName} · ${o.email}`
                              : "—"}
                          </td>
                          <td className="text-right">
                            {daysLeft === null ? (
                              "—"
                            ) : active ? (
                              <Badge color={badgeColor} pill className="font-weight-medium">
                                {intl.formatMessage(
                                  { id: "app.adminDashboard.freeTrial.daysLeftValue" },
                                  { n: daysLeft }
                                )}
                              </Badge>
                            ) : (
                              <Badge color="secondary" pill>
                                {intl.formatMessage({
                                  id: "app.adminDashboard.freeTrial.statusEnded",
                                })}
                              </Badge>
                            )}
                          </td>
                          <td className="small">
                            {lastDisplay || (
                              <span className="text-muted">
                                {intl.formatMessage({
                                  id: "app.adminDashboard.freeTrial.noLastLogin",
                                })}
                              </span>
                            )}
                          </td>
                          <td className="small">
                            {row.plan ? (
                              intl.formatMessage({ id: row.plan.name })
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </div>

          <div className="wembii-admin-panel">
            <div className="wembii-admin-panel__title">
              {intl.formatMessage({ id: "app.adminDashboard.panel.filters" })}
            </div>
            <div className="wembii-admin-panel__body">
              <div className="wembii-admin-panel__row wembii-admin-panel__row--dates">
                <div className="wembii-admin-panel__field wembii-admin-panel__field--date">
                  <label className="wembii-admin-field-label">
                    {intl.formatMessage({ id: "app.adminDashboard.filter.dateFrom" })}
                  </label>
                  <Input
                    type="date"
                    bsSize="sm"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="wembii-admin-panel__field wembii-admin-panel__field--date">
                  <label className="wembii-admin-field-label">
                    {intl.formatMessage({ id: "app.adminDashboard.filter.dateTo" })}
                  </label>
                  <Input
                    type="date"
                    bsSize="sm"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    min={dateFrom || undefined}
                  />
                </div>
                <div className="wembii-admin-panel__quick">
                  <Button
                    size="sm"
                    outline
                    className="wembii-admin-chip"
                    onClick={() => setPresetLastDays(7)}
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.filter.preset7d" })}
                  </Button>
                  <Button
                    size="sm"
                    outline
                    className="wembii-admin-chip"
                    onClick={() => setPresetLastDays(30)}
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.filter.preset30d" })}
                  </Button>
                  <Button
                    size="sm"
                    outline
                    className="wembii-admin-chip"
                    onClick={setPresetThisMonth}
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.filter.presetMonth" })}
                  </Button>
                  <Button
                    size="sm"
                    color="link"
                    className="wembii-admin-panel__clear-dates text-muted"
                    onClick={clearDateFilters}
                    disabled={!dateFilterActive}
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.filter.clearDates" })}
                  </Button>
                </div>
              </div>

              <div className="wembii-admin-panel__divider" aria-hidden />

              <div className="wembii-admin-panel__row wembii-admin-panel__row--search">
                <div className="wembii-admin-panel__field wembii-admin-panel__field--search">
                  <label className="wembii-admin-field-label">
                    {intl.formatMessage({ id: "app.adminDashboard.filter.search" })}
                  </label>
                  <Input
                    bsSize="sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: "app.adminDashboard.filter.searchPlaceholder",
                    })}
                  />
                </div>
                <div className="wembii-admin-panel__field wembii-admin-panel__field--plan">
                  <label className="wembii-admin-field-label">
                    {intl.formatMessage({ id: "app.adminDashboard.filter.plan" })}
                  </label>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                  >
                    <option value="__all__">
                      {intl.formatMessage({ id: "app.adminDashboard.filter.allPlans" })}
                    </option>
                    {planOptions.map((p) => (
                      <option key={p} value={p}>
                        {p.startsWith("app.") ? intl.formatMessage({ id: p }) : p}
                      </option>
                    ))}
                  </Input>
                </div>
              </div>
            </div>
          </div>

          <h5 className="wembii-admin-section-title">
            {intl.formatMessage({ id: "app.adminDashboard.section.companies" })}
          </h5>
          <div className="table-responsive wembii-admin-table-wrap mb-5">
            <Table hover size="sm" className="wembii-admin-table">
              <thead>
                <tr>
                  <th
                    className="sortable"
                    onClick={() => toggleSort("name")}
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.table.company" })}{" "}
                    {sort.key === "name" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th
                    className="sortable"
                    onClick={() => toggleSort("plan")}
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.table.plan" })}{" "}
                    {sort.key === "plan" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th>{intl.formatMessage({ id: "app.adminDashboard.table.owner" })}</th>
                  <th>{intl.formatMessage({ id: "app.adminDashboard.table.reseller" })}</th>
                  <th
                    className="sortable text-right"
                    onClick={() => toggleSort("credits")}
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.table.aiCredits" })}{" "}
                    {sort.key === "credits" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th>{intl.formatMessage({ id: "app.adminDashboard.table.created" })}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row: CompanyPayload) => {
                  const c = row.company as CompanyExtras;
                  const credits = creditsByCompany.get(c.id);
                  const created =
                    c.createdAt ||
                    (c as { created_at?: string }).created_at ||
                    null;
                  return (
                    <tr key={c.id}>
                      <td className="font-weight-medium">{c.name}</td>
                      <td>
                        {row.plan ? (
                          intl.formatMessage({ id: row.plan.name })
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="small">
                        {row.owner
                          ? `${row.owner.firstName} ${row.owner.lastName} · ${row.owner.email}`
                          : "—"}
                      </td>
                      <td className="small">
                        {c.reseller?.name || "—"}
                      </td>
                      <td className="text-right wembii-admin-num">
                        {credits !== undefined ? (
                          credits
                        ) : loadingCredits ? (
                          "…"
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="small text-muted">
                        {created
                          ? new Date(created).toLocaleDateString()
                          : intl.formatMessage({ id: "app.adminDashboard.noCreatedAt" })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="mt-5">
            <h5
              className={`wembii-admin-section-title ${
                dateFilterActive ? "mb-2" : "mb-3"
              }`}
            >
              {intl.formatMessage({ id: "app.adminDashboard.usersSample" })}
            </h5>
            {dateFilterActive && (
              <p className="small text-muted mb-3">
                {intl.formatMessage({ id: "app.adminDashboard.usersSampleDateHint" })}
              </p>
            )}
            <div className="table-responsive wembii-admin-users-table">
            <Table size="sm" className="wembii-admin-table">
              <thead>
                <tr>
                  <th>{intl.formatMessage({ id: "app.adminDashboard.table.userName" })}</th>
                  <th>Email</th>
                  <th>{intl.formatMessage({ id: "app.adminDashboard.table.userPlan" })}</th>
                </tr>
              </thead>
              <tbody>
                {(dateFilterActive ? userSampleFiltered : userSample).map((u) => (
                  <tr key={u.id}>
                    <td>
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="small">{u.email}</td>
                    <td className="small">
                      {u.userPlan?.name
                        ? intl.formatMessage({ id: u.userPlan.name })
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          </div>
        </Container>
      </Content>
    </Layout>
  );
}
